import { createResource, createSignal, createMemo, For, Show } from 'solid-js';
import TimeInput24 from '../components/TimeInput24.jsx';
import SplAdmin from './SplAdmin.jsx';

// Daftar Lembur — admin oversight listing of every lembur request for the
// active company, searchable/filterable by date period and keterangan (also
// name/NIK). Each request is normally approved by the chosen approver in the
// portal; an HR admin can decide here as the FALLBACK (e.g. the directed
// approver is away). Filtering is client-side over the fetched rows.

async function getJSON(url) {
  const res = await fetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || ('HTTP ' + res.status));
  return data;
}

const STATUS_TONE = { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'info' };
const STATUS_LABEL = { pending: 'menunggu', approved: 'disetujui', rejected: 'ditolak', cancelled: 'dibatalkan' };

export default function DaftarLembur() {
  // Top-right tab selector splits this once-cluttered page into three panels.
  const [tab, setTab] = createSignal('approval'); // 'approval' | 'daftar' | 'manual'
  const [all, { refetch }] = createResource(() => getJSON('/api/lembur'));
  const [from, setFrom] = createSignal('');
  const [to, setTo] = createSignal('');
  const [q, setQ] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  // Per-row approved-hours edits (id → {start,end}); default to the requested
  // window. The admin may tweak these before Setujui to override the hours.
  const [editTimes, setEditTimes] = createSignal({});
  const timesOf = (r) => editTimes()[r.id] ?? { start: r.start, end: r.end };
  function setTime(r, field, val) {
    setEditTimes({ ...editTimes(), [r.id]: { ...timesOf(r), [field]: val } });
  }

  // HR-admin fallback: decide an overtime request when its directed approver
  // can't. Approves with the (possibly edited) hours — the server treats an
  // unchanged window as "approved as requested". A rejection needs a reason.
  async function decide(r, approve) {
    let note = '';
    if (!approve) {
      note = prompt('Alasan penolakan:') || '';
      if (!note.trim()) return;
    }
    const t = timesOf(r);
    setBusy(true);
    const res = await fetch('/api/lembur/decide', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: r.id, approve, note, overrideStart: t.start, overrideEnd: t.end }),
    });
    setBusy(false);
    if (res.ok) refetch(); else alert((await res.json().catch(() => ({}))).error);
  }

  const filtered = createMemo(() => {
    const f = from(), t = to(), kw = q().trim().toLowerCase();
    return (all() ?? []).filter((r) =>
      (!f || r.workDate >= f) && (!t || r.workDate <= t) &&
      (!kw || (r.reason || '').toLowerCase().includes(kw)
            || (r.name || '').toLowerCase().includes(kw)
            || (r.nik || '').toLowerCase().includes(kw)));
  });

  const dirty = () => from() || to() || q();

  // ── Rekap tab: per-employee approved-overtime totals over a free date range
  // (Jam1/Jam2/total, grand-total footer). Server-derived — the same
  // DeriveOvertime the run prefill uses, so numbers match the slips.
  const [rkFrom, setRkFrom] = createSignal('');
  const [rkTo, setRkTo] = createSignal('');
  const [rkQ, setRkQ] = createSignal('');
  const [rkData, setRkData] = createSignal(null);
  const [rkBusy, setRkBusy] = createSignal(false);
  const [rkErr, setRkErr] = createSignal('');
  async function loadRekap() {
    if (!rkFrom() || !rkTo()) { setRkErr('Isi tanggal mulai dan tanggal akhir dulu.'); return; }
    setRkBusy(true); setRkErr(''); setRkData(null);
    try {
      setRkData(await getJSON(`/api/lembur/rekap?start=${rkFrom()}&end=${rkTo()}`));
    } catch (e) { setRkErr(e.message); }
    setRkBusy(false);
  }
  const rkRows = createMemo(() => {
    const kw = rkQ().trim().toLowerCase();
    const rows = rkData()?.rows ?? [];
    if (!kw) return rows;
    return rows.filter((r) =>
      r.nik.toLowerCase().includes(kw) || (r.name || '').toLowerCase().includes(kw)
      || (r.department || '').toLowerCase().includes(kw));
  });
  function exportRekap() {
    const a = document.createElement('a');
    a.href = `/api/lembur/rekap/export?start=${rkFrom()}&end=${rkTo()}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return (
    <>
    <div class="page-head">
      <h2>Daftar Lembur</h2>
      <div class="spacer" />
      <div class="seg">
        <button class="seg-item" classList={{ active: tab() === 'approval' }} onClick={() => setTab('approval')}>Approval SPL</button>
        <button class="seg-item" classList={{ active: tab() === 'daftar' }} onClick={() => setTab('daftar')}>Daftar SPL</button>
        <button class="seg-item" classList={{ active: tab() === 'manual' }} onClick={() => setTab('manual')}>SPL Manual</button>
        <button class="seg-item" classList={{ active: tab() === 'rekap' }} onClick={() => setTab('rekap')}>Rekap</button>
      </div>
    </div>

    {/* SPL Manual (create) + Daftar SPL (list) live in SplAdmin, kept mounted so
        a create refreshes the list; it renders the panel matching the active tab. */}
    <SplAdmin tab={tab()} />

    <Show when={tab() === 'approval'}>
    <div class="card card-pad">
      <div class="ky-vars-head">
        <h3 class="kg-hist-title">Pengajuan Lembur</h3>
        <Show when={all()}>
          <span class="kg-meta-text" style="margin-left:8px">{filtered().length} / {all().length} pengajuan</span>
        </Show>
        <div class="spacer" />
        <button class="btn ghost" disabled={all.loading} onClick={refetch}>{all.loading ? 'Memuat…' : 'Muat ulang'}</button>
      </div>

      <p class="ky-vars-note">
        Semua pengajuan lembur perusahaan ini beserta status persetujuannya. Persetujuan utama
        dilakukan penyetuju yang dipilih karyawan di portal; admin HR dapat menyetujui/menolak di
        sini sebagai cadangan bila penyetuju berhalangan.
      </p>

      <div class="lb-filter">
        <label class="kg-field"><span>Dari tanggal</span>
          <input type="date" value={from()} onInput={(e) => setFrom(e.currentTarget.value)} /></label>
        <label class="kg-field"><span>Sampai tanggal</span>
          <input type="date" value={to()} onInput={(e) => setTo(e.currentTarget.value)} /></label>
        <label class="kg-field lb-search"><span>Cari keterangan / nama / NIK</span>
          <input type="text" placeholder="kata kunci…" value={q()} onInput={(e) => setQ(e.currentTarget.value)} /></label>
        <Show when={dirty()}>
          <button class="btn ghost" onClick={() => { setFrom(''); setTo(''); setQ(''); }}>Reset</button>
        </Show>
      </div>

      <Show when={all.error}>
        <div class="kg-verdict fail">{String(all.error.message || all.error)}</div>
      </Show>

      <div class="pg-grid-wrap">
        <table class="data">
          <thead>
            <tr>
              <th>Karyawan</th><th>Departemen</th><th>Tanggal</th><th>Jam</th>
              <th>Keterangan</th><th>Bukti</th><th>Status</th><th>Diputuskan</th><th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <For each={filtered()}>{(r) =>
              <tr>
                <td><div class="kg-code">{r.nik}</div><div class="kg-sub">{r.name}</div></td>
                <td>{r.department || <span class="vg-dim">—</span>}</td>
                <td class="ky-nik">{r.workDate}</td>
                <td class="ky-nik">
                  <Show
                    when={r.status === 'pending'}
                    fallback={<>{r.start}–{r.end}<Show when={r.approvedStart}><div class="kg-sub">disetujui {r.approvedStart}–{r.approvedEnd}</div></Show></>}
                  >
                    <div class="lb-time-edit">
                      <TimeInput24 value={timesOf(r).start} disabled={busy()} onChange={(v) => setTime(r, 'start', v)} />
                      <span>–</span>
                      <TimeInput24 value={timesOf(r).end} disabled={busy()} onChange={(v) => setTime(r, 'end', v)} />
                    </div>
                  </Show>
                </td>
                <td><Show when={r.reason} fallback={<span class="vg-dim">—</span>}><span class="lb-reason">{r.reason}</span></Show></td>
                <td>
                  <For each={r.files}>{(f) =>
                    <a class="kary-file" href={`/api/lembur/file/${f.id}`} target="_blank" rel="noopener" title={f.origName}>📎</a>
                  }</For>
                </td>
                <td>
                  <span class={`badge ${STATUS_TONE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                  <Show when={r.status === 'pending' && r.targetApproverName}>
                    <div class="kg-sub">→ {r.targetApproverName}</div>
                  </Show>
                </td>
                <td class="kg-meta-text">
                  <Show when={r.decidedAt} fallback="—">
                    {r.decidedAt}<Show when={r.approverNik}><div class="kg-sub">oleh {r.approverNik}</div></Show>
                    <Show when={r.status === 'rejected' && r.decisionNote}><div class="kg-sub">{r.decisionNote}</div></Show>
                  </Show>
                </td>
                <td class="num">
                  <Show when={r.status === 'pending'} fallback={<span class="vg-dim">—</span>}>
                    <div class="lb-actions">
                      <button class="btn primary" disabled={busy()} onClick={() => decide(r, true)}>Setujui</button>
                      <button class="btn ghost" disabled={busy()} onClick={() => decide(r, false)}>Tolak</button>
                    </div>
                  </Show>
                </td>
              </tr>
            }</For>
            <Show when={all() && filtered().length === 0}>
              <tr><td colspan="9" class="kg-meta-text">Tidak ada pengajuan lembur{dirty() ? ' yang cocok dengan filter' : ''}.</td></tr>
            </Show>
          </tbody>
        </table>
      </div>
    </div>
    </Show>

    {/* ── Rekap: per-employee approved-overtime totals over a free date range ── */}
    <Show when={tab() === 'rekap'}>
    <div class="card card-pad">
      <div class="ky-vars-head">
        <h3 class="kg-hist-title">Rekap Lembur per Karyawan</h3>
        <Show when={rkData()}>
          <span class="kg-meta-text" style="margin-left:8px">{rkRows().length} karyawan · {rkData().start} — {rkData().end}</span>
        </Show>
        <div class="spacer" />
        <Show when={rkData()?.rows?.length > 0}>
          <button class="btn" onClick={exportRekap} title="Export rekap ini ke Excel">⤓ Export Excel</button>
        </Show>
      </div>

      <p class="ky-vars-note">
        Total jam lembur <b>disetujui</b> per karyawan untuk rentang tanggal bebas (tanpa perlu run gaji),
        dibagi Jam Lembur 1 (jam pertama per hari) dan Jam Lembur 2 (sisanya) — perhitungan yang sama
        dengan pengisian input gaji, jadi angkanya cocok dengan slip.
      </p>

      <div class="lb-filter">
        <label class="kg-field"><span>Dari tanggal</span>
          <input type="date" value={rkFrom()} onInput={(e) => setRkFrom(e.currentTarget.value)} /></label>
        <label class="kg-field"><span>Sampai tanggal</span>
          <input type="date" value={rkTo()} onInput={(e) => setRkTo(e.currentTarget.value)} /></label>
        <button class="btn primary" disabled={rkBusy()} onClick={loadRekap}>{rkBusy() ? 'Menghitung…' : 'Tampilkan'}</button>
        <label class="kg-field lb-search"><span>Cari nama / NIK / departemen</span>
          <input type="text" placeholder="kata kunci…" value={rkQ()} onInput={(e) => setRkQ(e.currentTarget.value)} /></label>
      </div>

      <Show when={rkErr()}><div class="kg-verdict fail">{rkErr()}</div></Show>

      <Show when={rkData()}>
        <div class="pg-grid-wrap">
          <table class="data">
            <thead>
              <tr>
                <th>Karyawan</th><th>Departemen</th>
                <th class="num">Hari SPL</th>
                <th class="num">Jam Lembur 1</th>
                <th class="num">Jam Lembur 2</th>
                <th class="num">Total Jam</th>
              </tr>
            </thead>
            <tbody>
              <For each={rkRows()} fallback={
                <tr><td colspan="6" class="kg-meta-text">Tidak ada lembur disetujui pada rentang ini{rkQ() ? ' yang cocok dengan filter' : ''}.</td></tr>
              }>{(r) =>
                <tr>
                  <td><div class="kg-code">{r.nik}</div><div class="kg-sub">{r.name}</div></td>
                  <td>{r.department || <span class="vg-dim">—</span>}</td>
                  <td class="num">{r.splDays}</td>
                  <td class="num">{r.jam1}</td>
                  <td class="num">{r.jam2Hours}</td>
                  <td class="num"><b>{r.totalHours}</b></td>
                </tr>
              }</For>
            </tbody>
            <Show when={!rkQ() && rkData().rows.length > 0}>
              <tfoot>
                <tr class="lb-rekap-total">
                  <td colspan="2"><b>Total ({rkData().rows.length} karyawan)</b></td>
                  <td class="num"><b>{rkData().totalSplDays}</b></td>
                  <td class="num"><b>{rkData().totalJam1}</b></td>
                  <td class="num"><b>{rkData().totalJam2Hours}</b></td>
                  <td class="num"><b>{rkData().totalHours}</b></td>
                </tr>
              </tfoot>
            </Show>
          </table>
        </div>
      </Show>
    </div>
    </Show>
    </>
  );
}
